import {
  faCrosshairs,
  faEye,
  faLock,
  faLockOpen,
  faRotateRight,
  faTimes,
  faUpload
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { Circle, Image, Layer, Stage } from "react-konva";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ModalComponent from "../../../../../core/components/modal/modal.component";
import SkeletonComponent from "../../../../../shared/custom-spinner/skeleton.component";
import { dataUrlToFile } from "../../../../../shared/helpers/data-url-to-file";
import { safeAtob } from "../../../../../shared/helpers/enode-decode-string";
import { FloorShape } from "../../../../../shared/models/floorPlan";

import exitFullScreen from "../../../../../assets/images/arrows-collapse.png";
import fullScreen from "../../../../../assets/images/arrows-fullscreen.png";
import zoomIn from "../../../../../assets/images/zoom-in.png";
import zoomOut from "../../../../../assets/images/zoom-out.png";
import circleLightFault from "../canvas/images/circle-light-fault.png";
import circleLightOff from "../canvas/images/circle-light-off.png";
import circleLightOn from "../canvas/images/circle-light-on.png";
import circleLightOffGreen from "../canvas/images/circle-light-update-off.png";
import selectLocation from "../canvas/images/crosshair.png";

import {
  getAssetSingleAssetByCode,
  updateAssetLocation,
} from "../../../../../store/slices/asset.slice";
import {
  getFloorAssetsAndShapes,
  getFloorDetails,
  getFloorPlanByAsset,
  updateFloorPlan,
} from "../../../../../store/slices/fault.slice";
import {
  setAttemptedRoute,
  setUnsavedDetectorInitiated,
} from "../../../../../store/slices/route-state.slice";
import "./floorPlan.scss";

import { B2Button } from "@hdb/biome-2";
import { Spinner } from "react-bootstrap";
import axiosInstance from "src/core/api/axiosClient";
import {
  getLastDaylightReadingByAsset,
  getLastMotionReadingByAsset,
} from "src/core/api/services/asset.service";
import { getAllDisplayLocations } from "src/core/api/services/display-location.service";
import { DynamicValuesEnum } from "src/core/enums/dynamic-values.enum";
import {
  lightOffColors,
  lightOnColors,
} from "src/core/enums/floorplan-color-enums";
import HasPermission from "src/core/guards/has-permission";
import SelectComponent from "src/shared-elements/select/select-component";
import { calculateAllDynamicValues } from "src/shared/helpers/dynamic-values.helper";
import {
  calculateImageDimensions,
  createPinchZoomHandlers,
  getZoomStepIn,
  getZoomStepOut,
  handleShapeMouseEnter,
  handleShapeMouseLeave,
  handleStageWheel as handleStageWheelHelper,
} from "src/shared/helpers/floorplan.helpers";
import {
  imageFileTypes,
  validateImageFile,
} from "src/shared/helpers/validate-Files";
import { setAssetRemovedOrUpdated } from "src/store/slices/dashboard.slice";
import {
  getAllFloorsByBlockCode,
  removeAssetFloor,
} from "src/store/slices/floor.slice";
import { getBlocksBySiteCode } from "src/store/slices/location-block.slice";
import DashboardFloorplanWildcardSearchingComponent from "../dashboard-floorplan-wildcard-searching.component";
import useOutsideClick from "../hooks/useOutsideClick";
import onCircleInitial from "./images/circle-light-off.png";
import gatewayFault from "./images/gateway-fault.png";
import gatewayInitial from "./images/gateway-initial.png";
import gatewayOffline from "./images/gateway-offline.png";
import gatewayOnline from "./images/gateway-online.png";
import photocellOnline from "./images/PhotocellSensorGreen.png";
import photocellOffline from "./images/PhotocellSensorGrey.png";
import photocellOff from "./images/PhotocellSensorRed.png";

const UpdateFloorPlanComponent = ({ refetchFloorAssetsAndShapes }: any) => {
  const [tooltip, setTooltip] = useState({
    visible: false,
    content: "",
    selectedItemId: null,
    x: 0,
    y: 0,
  });

  const location: any = useLocation();
  const { pathname } = location;

  const [locationOptions, setLocationOptions] = useState<any>([]);
  const [levelOptions, setLevelOptions] = useState<any>([]);
  const [isDraggingItemsEnable, setIsDraggingItemsEnable] = useState<boolean>(true);

  const [isSelectMode, setIsSelectMode] = useState(false);

  const floorResponse = useSelector((state: any) => {
    return state.floor.floorByBlockCode;
  });

  const isLoadingFloorByBlockCode = useSelector((state: any) => {
    return state.floor.isLoadingFloorByBlockCode;
  });

  const locationData = useSelector((state: any) => {
    return state.locationBlock.blocksBySiteCode;
  });

  const assetDetails = useSelector((state: any) => {
    return state.asset.assetDetailsData;
  });

  const [selectedWhileHovered, setSelectedWhileHovered] = useState(null as any);

  const [hovered, setHovered] = useState(false);

  const [hoverState, setHoverState] = useState({
    visible: false,
    x: 0,
    y: 0,
  });

  // Hover timer to show hover tooltip only after a short delay
  const hoverTimeoutRef = useRef<number | null>(null);
  const HOVER_DELAY = 500; // milliseconds

  // Cleanup hover timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        window.clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };
  }, []);

  const navigate = useNavigate();
  const [image, setImage] = useState<any>(null);
  const [shapes, setShapes] = useState<FloorShape[]>([]);
  const [changedShapes, setChangedShapes] = useState<FloorShape[]>([]);
  // const [similarFloorOptions, setSimilarFloorOptions] = useState<any>([]);
  const [selectedSimilarFloors] = useState<any>([]);
  const stageRef: any = useRef();
  const pinchStateRef: any = useRef(null);
  const layerRef: any = useRef();
  const dispatch = useDispatch();
  const [zoomLevel, setZoomLevel] = useState(1);

  // Calculated values based on zoom level
  const [dynamicCircle, setDynamicCircle] = useState(DynamicValuesEnum.CIRCLE);
  const [circlePositionOffset, setCirclePositionOffset] = useState(0);

  // Gateway dynamics object containing width, height and offset calculation
  const [dynamicGatewaySize] = useState(
    DynamicValuesEnum.GATEWAY_SIZE
  );
  const [dynamicGatewayOffset, setDynamicGatewayOffset] = useState(0);
  const [dynamicPhotocellSize, setDynamicPhotocellSize] = useState(
    DynamicValuesEnum.PHOTOCELL_SIZE
  );
  const [dynamicPhotocellOffset, setDynamicPhotocellOffset] = useState(0);

  const params = useParams();
  const blockCode: any = params.blockCode ? safeAtob(params.blockCode) : null;
  const floor = params?.floor;
  const [tempPosition, setTempPosition] = useState<any>(null);

  const [isLoadingMotionAndDaylightValue, setIsLoadingMotionAndDaylightValue] =
    useState(false);
  const [isLoadingAssetDetails, setIsLoadingAssetDetails] = useState(false);
  const [isLoadingBlocksBySiteCode, setIsLoadingBlocksBySiteCode] =
    useState(false);

  // const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [globalSelectedShape, setGlobalSelectedShape] = useState(null as any);
  const [newValueOnStageMove, setNewValueOnStageMove] = useState({
    x: 0,
    y: 0,
  });

  const handleStageDrag = (x: any, y: any) => {
    dispatch(setUnsavedDetectorInitiated(true));
    setNewValueOnStageMove({ x, y });
    setGlobalSelectedShape(null);
  };

  const handleLayerSelect = (e: any) => {
    if (!isSelectMode) return;

    const stage = e.target.getStage();
    const layer = layerRef.current;
    if (!stage || !layer) return;

    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const inverted = layer.getAbsoluteTransform().copy().invert();
    const localPoint = inverted.point(pointer);

    setTempPosition({
      x: localPoint.x,
      y: localPoint.y,
    });
  };

  // // Wheel zoom while hovering the stage — delegating to shared helper
  const handleStageWheel = (e: any) => {
    handleStageWheelHelper({
      e,
      stageRef,
      zoomLevel,
      setZoomLevel,
      newValueOnStageMove,
      setNewValueOnStageMove,
      setTooltip,
      tooltip,
      setHoverState,
      hoverState,
      setGlobalSelectedShape,
      setSelectedWhileHovered,
      dynamicValuesEnumMaxZoom: DynamicValuesEnum.MAX_ZOOM,
    });
  };

  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 530,
  });

  const file1 = "image1.png";
  const gatewayFileFault: any = useMemo(
    () => dataUrlToFile(gatewayFault, file1),
    []
  );

  const file2 = "image1.png";
  const gatewayFileInitial: any = useMemo(
    () => dataUrlToFile(gatewayInitial, file2),
    []
  );

  const file3 = "image1.png";
  const gatewayFileOffline: any = useMemo(
    () => dataUrlToFile(gatewayOffline, file3),
    []
  );

  const file4 = "image1.png";
  const gatewayFileOnline: any = useMemo(
    () => dataUrlToFile(gatewayOnline, file4),
    []
  );

  const file5 = "image5.png";
  const photocellFileOnline: any = useMemo(
    () => dataUrlToFile(photocellOnline, file5),
    []
  );

  const file6 = "image6.png";
  const photocellFileOffline: any = useMemo(
    () => dataUrlToFile(photocellOffline, file6),
    []
  );

  const file7 = "image7.png";
  const photocellFileOff: any = useMemo(
    () => dataUrlToFile(photocellOff, file7),
    []
  );

  // Light ON
  const filename2 = "image2.png";
  const onFaultFile: any = useMemo(
    () => dataUrlToFile(circleLightFault, filename2),
    []
  );

  const filename3 = "image3.png";
  const onInitialFile: any = useMemo(
    () => dataUrlToFile(onCircleInitial, filename3),
    []
  );

  const filename4 = "image4.png";
  const onOfflineFile: any = useMemo(
    () => dataUrlToFile(circleLightOffGreen, filename4),
    []
  );

  const filename5 = "image5.png";
  const onOnlineFile: any = useMemo(
    () => dataUrlToFile(circleLightOn, filename5),
    []
  );

  // OFF
  const filename6 = "image6.png";
  const offFaultFile: any = useMemo(
    () => dataUrlToFile(circleLightFault, filename6),
    []
  );

  const filename7 = "image7.png";
  const offInitialFile: any = useMemo(
    () => dataUrlToFile(circleLightOff, filename7),
    []
  );

  const filename8 = "image8.png";
  const offOfflineFile: any = useMemo(
    () => dataUrlToFile(circleLightOff, filename8),
    []
  );

  const filename9 = "image9.png";
  const offOnlineFile: any = useMemo(
    () => dataUrlToFile(circleLightOffGreen, filename9),
    []
  );

  const filename10 = "image10.png";
  const selectLocationFile: any = useMemo(
    () => dataUrlToFile(selectLocation, filename10),
    []
  );

  const [gatewayImageFileFault, setGatewayImageFileFault] =
    useState<any>(undefined);
  const [gatewayImageFileOffline, setGatewayImageFileOffline] =
    useState<any>(undefined);
  const [gatewayImageFileInitial, setGatewayImageFileInitial] =
    useState<any>(undefined);
  const [gatewayImageFileOnline, setGatewayImageFileOnline] =
    useState<any>(undefined);
  const [photocellImageFileOnline, setPhotocellImageFileOnline] =
    useState<any>(undefined);
  const [photocellImageFileOffline, setPhotocellImageFileOffline] =
    useState<any>(undefined);
  const [photocellImageFileOff, setPhotocellImageFileOff] =
    useState<any>(undefined);

  const [onFaultImageFile, setOnFaultImageFile] = useState<any>(undefined);
  const [onInitialImageFile, setOnInitialImageFile] = useState<any>(undefined);
  const [onOfflineImageFile, setOnOfflineImageFile] = useState<any>(undefined);
  const [onOnlineImageFile, setOnOnlineImageFile] = useState<any>(undefined);
  const [offFaultImageFile, setOffFaultImageFile] = useState<any>(undefined);
  const [offInitialImageFile, setOffInitialImageFile] =
    useState<any>(undefined);
  const [offOfflineImageFile, setOffOfflineImageFile] =
    useState<any>(undefined);
  const [offOnlineImageFile, setOffOnlineImageFile] = useState<any>(undefined);
  const [selectLocationIcon, setselectLocationIcon] =
    useState<any>(undefined);
  const [selectedAssetCodeForHighlight, setSelectedAssetCodeForHighlight] =
    useState("");

  // Track which shape is being dragged
  const [draggingShapeId, setDraggingShapeId] = useState<string | null>(null);

  const [locations, setLocations] = useState<any>([]);
  const [storeLocations, setStoreLocations] = useState<any>([]);
  const [isUpdateButtonInTooltipVisible, setIsUpdateButtonInTooltipVisible] =
    useState(false);

  /*   const { data: similarFloors, isLoading: isLoadingSimilarFloors } = useQuery({
      queryKey: ["getFloorsWithSimilarPlan", blockCode, floor],
      queryFn: ({ signal }) => getFloorsWithSimilarPlan(blockCode, floor, signal),
    }); */

  const { data: locationsData, isLoading: isLoadingLocations } = useQuery({
    queryKey: ["getAllDisplayLocations"],
    queryFn: ({ signal }) => getAllDisplayLocations(0, 0, signal),
  });

  useEffect(() => {
    const locations = locationsData?.data?.map((location: any) => ({
      title: location?.name,
      value: location?.id,
    }));

    setLocations(locations);
  }, [locationsData]);

  /*   useEffect(() => {
      const townCouncils = similarFloors?.data?.map((floor: any) => ({
        cat: floor?.floorCode,
        key: floor?.floorCode,
      }));
  
      setSimilarFloorOptions(townCouncils);
    }, [similarFloors]); */

  /*   useEffect(() => {
    if (changedShapes?.length > 0) {
      dispatch(setIsFloorPlanUpdated(true));
    } else {
      dispatch(setIsFloorPlanUpdated(false));
    }
  }, [changedShapes]); */

  const updateContainerDimensions = () => {
    const canvasDiv = document.querySelector("#canvas");
    if (canvasDiv) {
      const rect = canvasDiv.getBoundingClientRect();
      setContainerDimensions({
        width: rect.width,
        height: 530,
      });
    }
  };

  useEffect(() => {
    updateContainerDimensions();
    dispatch(getAllFloorsByBlockCode());
  }, []);

  useEffect(() => {
    const floors = floorResponse?.data?.map((floor: any) => ({
      title: floor?.displayLevel,
      value: floor?.displayLevel,
    }));

    setLevelOptions(floors);
  }, [floorResponse]);

  useEffect(() => {
    dispatch(getFloorDetails({ floorCode: floor, blockCode }));
    dispatch(getFloorAssetsAndShapes({ floorCode: floor, blockCode }));
  }, [dispatch, floor]);

  useEffect(() => {
    const blocks = locationData?.data?.map((block: any) => ({
      title: block?.blockCode,
      value: block?.blockCode,
    }));

    setLocationOptions(blocks);
  }, [locationData]);

  const floorDetails = useSelector((state: any) => {
    return state.fault.floorDetails;
  });

  const isLoadingFloorDetails = useSelector((state: any) => {
    return state.fault.isLoadingFloorDetails;
  });

  const floorAssetsAndShapes = useSelector((state: any) => {
    return state.fault.floorAssetsAndShapes;
  });

  let isLoadingFloorAssetsAndShapes = useSelector((state: any) => {
    return state.fault.isLoadingFloorAssetsAndShapes;
  });

  useEffect(() => {
    setImage(null);
    if (floorDetails?.data?.floorImage) {
      const imageFile = floorDetails?.data?.floorImage;

      const isBase64 =
        typeof imageFile === "string" && imageFile.startsWith("iVBOR");
      const src = isBase64 ? `data:image/png;base64,${imageFile}` : imageFile;

      const imageElement = new window.Image();
      imageElement.src = src;
      imageElement.onload = () => setImage(imageElement);

      // setImage(imageElement);
    }
  }, [blockCode, floor, floorDetails]);

  // gateway
  useEffect(() => {
    const imageUrl = URL.createObjectURL(gatewayFileFault);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setGatewayImageFileFault(imageElement);
  }, [gatewayFileFault]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(gatewayFileInitial);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setGatewayImageFileInitial(imageElement);
  }, [gatewayFileInitial]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(gatewayFileOffline);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setGatewayImageFileOffline(imageElement);
  }, [gatewayFileOffline]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(gatewayFileOnline);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setGatewayImageFileOnline(imageElement);
  }, [gatewayFileOnline]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(photocellFileOnline);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setPhotocellImageFileOnline(imageElement);
  }, [photocellFileOnline]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(photocellFileOffline);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setPhotocellImageFileOffline(imageElement);
  }, [photocellFileOffline]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(photocellFileOff);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setPhotocellImageFileOff(imageElement);
  }, [photocellFileOff]);

  // light on
  useEffect(() => {
    const imageUrl = URL.createObjectURL(onFaultFile);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setOnFaultImageFile(imageElement);
  }, [onFaultFile]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(onInitialFile);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setOnInitialImageFile(imageElement);
  }, [onInitialFile]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(onOfflineFile);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setOnOfflineImageFile(imageElement);
  }, [onOfflineFile]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(onOnlineFile);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setOnOnlineImageFile(imageElement);
  }, [onOnlineFile]);

  // light off
  useEffect(() => {
    const imageUrl = URL.createObjectURL(offFaultFile);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setOffFaultImageFile(imageElement);
  }, [offFaultFile]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(offInitialFile);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setOffInitialImageFile(imageElement);
  }, [offInitialFile]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(offOfflineFile);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setOffOfflineImageFile(imageElement);
  }, [offOfflineFile]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(offOnlineFile);
    const imageElement = new window.Image();
    imageElement.src = imageUrl;

    setOffOnlineImageFile(imageElement);
  }, [offOnlineFile]);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(selectLocationFile);
    const imageElement = new window.Image();
    imageElement.onload = () => setselectLocationIcon(imageElement);
    imageElement.src = imageUrl;

    return () => {
      imageElement.onload = null;
      URL.revokeObjectURL(imageUrl);
    };
  }, [selectLocationFile]);

  useEffect(() => {
    isLoadingFloorAssetsAndShapes = true;

    let row = 0;
    let col = 0;
    const itemsPerRow = 43;
    const yStep = 20;
    const xStep = 25;

    const updatedShapes: FloorShape[] = floorAssetsAndShapes?.data?.map(
      (item: FloorShape) => {
        const x = item?.x
          ? item.x / zoomLevel
          : (col * xStep) / zoomLevel + xStep;

        const y = item?.y ? item.y / zoomLevel : (row * yStep) / zoomLevel;

        col++;

        if (col >= itemsPerRow) {
          col = 0;
          row++;
        }

        return {
          id: item?.assetCode,
          status: item?.status,
          x,
          y,
          assetCode: item?.assetCode,
          assetTypeId: item?.assetTypeId,
          floorLocation: item?.floorLocation,
          assetType: item?.assetType,
          brightLevel: item?.brightLevel,
          dimLevel: item?.dimLevel,
          faultType: item?.faultType,
          sensorDisplayName: item?.sensorDisplayName,
          sequenceNo: item?.sequenceNo,
          isLightOn: item?.isLightOn,
          motionCount: item?.motionCount,
          displayLocationId: item?.displayLocationId,
          daylight: item?.daylight,
          lightType: item?.lightType,
          parentAssetTypeId: item?.parentAssetTypeId,
        };
      }
    );

    setShapes(updatedShapes);
    isLoadingFloorAssetsAndShapes = false;
  }, [floorAssetsAndShapes]);

  // Update calculated values when zoom level changes
  useEffect(() => {
    // Calculate all dynamic values using the helper function
    const dynamicValues = calculateAllDynamicValues(zoomLevel);

    // Update all state values with calculated values
    setDynamicCircle(dynamicValues.circle);
    setCirclePositionOffset(dynamicValues.circlePositionOffset);
    // setDynamicGateway(dynamicValues.gateway.size);
    setDynamicGatewayOffset(dynamicValues.gateway.offset);
    setDynamicPhotocellSize(dynamicValues.photocell.size);
    setDynamicPhotocellOffset(dynamicValues.photocell.offset);
  }, [zoomLevel]);

  useEffect(() => {
    if (!selectedAssetCodeForHighlight?.length) return;

    const onlyAssetCodes = shapes?.map((shape) => shape?.assetCode);

    if (onlyAssetCodes?.includes(selectedAssetCodeForHighlight)) {
      toast.warning("Asset Already Annotated", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnFocusLoss: true,
        draggable: true,
        pauseOnHover: true,
      });

      return;
    }

    dispatch(
      getFloorPlanByAsset({
        assetCode: selectedAssetCodeForHighlight,
      })
    ).then((res: any) => {
      const newAddedObject = res?.payload?.[0];

      setShapes((prevShapes) => {
        // Filter objects to check for those starting with x >= 0 and y = 0
        const filteredShapes = prevShapes.filter(
          (shape) => shape.x >= 0 && shape.y === 0
        );

        // Create a copy of the newAddedObject to avoid modifying the original one
        const updatedObject = {
          ...newAddedObject,
          id: newAddedObject?.assetCode,
        };

        // Calculate the new x position based on the number of filtered objects
        const nextX = filteredShapes.length * 15;

        updatedObject.x = tempPosition?.x
          ? (tempPosition.x - 8) / assetScale.x
          : nextX;
        updatedObject.y = tempPosition?.y
          ? (tempPosition.y - 8) / assetScale.y
          : 0; // Always keep y as 0

        // Update the floorCode of the new object to match the current floor
        updatedObject.floorCode = floor;

        const newChangedShapes = [...changedShapes, updatedObject];

        setChangedShapes(newChangedShapes);
        setTempPosition(null);

        return [...prevShapes, updatedObject];
      });
    });
  }, [selectedAssetCodeForHighlight]);

  const handleZoomIn = () => {
    setZoomLevel(
      Math.min(DynamicValuesEnum.MAX_ZOOM, zoomLevel + getZoomStepIn(zoomLevel))
    );

    setTooltip({ ...tooltip, visible: false });
    setGlobalSelectedShape(null);

    setHoverState({ ...hoverState, visible: false });
    setSelectedWhileHovered(null);
  };

  const handleZoomOut = () => {
    setZoomLevel(Math.max(0.5, zoomLevel - getZoomStepOut(zoomLevel)));

    setTooltip({ ...tooltip, visible: false });
    setGlobalSelectedShape(null);
    setHoverState({ ...hoverState, visible: false });
    setSelectedWhileHovered(null);
  };

  // Touch handlers for smooth pinch-to-zoom / two-finger pan on mobile
  const { handleTouchStart, handleTouchMove, handleTouchEnd } =
    createPinchZoomHandlers(
      {
        stageRef,
        zoomLevel,
        setZoomLevel,
        dynamicValuesEnumMaxZoom: DynamicValuesEnum.MAX_ZOOM,
        dynamicValuesEnumMinZoom: 0.5,
        setNewValueOnStageMove,
      },
      pinchStateRef,
    );


  // Reset zoom and re-center the stage layer to top-left
  const handleResetZoom = () => {
    setZoomLevel(1);
    setNewValueOnStageMove({ x: 0, y: 0 });

    const stage: any = stageRef.current;
    if (stage) {
      try {
        const layers = stage.getLayers ? stage.getLayers() : [];
        const layer = layers?.length ? layers[0] : null;
        if (layer) {
          layer.position({ x: 0, y: 0 });
          layer.batchDraw ? layer.batchDraw() : layer.draw();
        }
      } catch (err) {
        // fail silently if Konva refs not ready
        // eslint-disable-next-line no-console
        console.warn("Could not reset layer position:", err);
      }
    }

    setTooltip({ ...tooltip, visible: false });
    setHoverState({ ...hoverState, visible: false });
    setGlobalSelectedShape(null);
    setSelectedWhileHovered(null);
  };

  const handleOnClick = async (id: any, e?: any) => {
    const selectedShape = shapes.find((shape) => shape.id === id);
    if (!selectedShape || !selectedShape?.assetCode) {
      setIsLoadingMotionAndDaylightValue(false);
      return;
    }

    const parentAssetTypeId = selectedShape?.parentAssetTypeId?.toLowerCase();

    // "1" and "44" for photocell sensor, "3" for gateway
    const offset =
      parentAssetTypeId === "3"
        ? dynamicGatewayOffset
        : parentAssetTypeId === "1" || parentAssetTypeId === "44"
          ? dynamicPhotocellOffset
          : circlePositionOffset;

    const adjustedX =
      (selectedShape?.x * assetScale.x + newValueOnStageMove?.x + offset) *
      zoomLevel;
    const adjustedY =
      (selectedShape?.y * assetScale.y + newValueOnStageMove?.y + offset) *
      zoomLevel;

    setTooltip({
      visible: true,
      content: `Asset ID: ${selectedShape?.assetCode}\nLocation: ${selectedShape?.floorLocation}\nType: ${selectedShape?.assetType}`,
      selectedItemId: id,
      x: handle.active && e ? e?.evt?.layerX - 8 : adjustedX,
      y: handle.active && e ? e?.evt?.layerY - 84 : adjustedY,
    });

    setGlobalSelectedShape(selectedShape);

    const assetDetailsPromise = (async () => {
      try {
        const res = await dispatch(
          getAssetSingleAssetByCode({ assetCode: selectedShape.assetCode })
        );

        setIsLoadingAssetDetails(false);

        if (res?.payload?.siteCode) {
          await dispatch(
            getBlocksBySiteCode({ siteCode: res.payload.siteCode })
          );
        }
      } catch (error) {
        console.error("Error fetching asset details/blocks:", error);
      } finally {
        setIsLoadingAssetDetails(false);
        setIsLoadingBlocksBySiteCode(false);
      }
    })();

    /*     dispatch(
      sendGetCommandToDms({
        assetCode: selectedShape.assetCode,
        module: Number(AssetCommandEnums.getLightIntensity),
      })
    ); */

    const motionAndDaylightPromise = (async () => {
      try {
        const [motionData, daylightData] = await Promise.all([
          getLastMotionReadingByAsset(selectedShape.assetCode, null),
          getLastDaylightReadingByAsset(selectedShape.assetCode, null),
        ]);

        const updatedShape = {
          ...selectedShape,
          motionCount:
            motionData?.data?.motionReading ?? selectedShape.motionCount,
          daylight:
            daylightData?.data?.daylightReading ?? selectedShape.daylight,
        };

        setGlobalSelectedShape(updatedShape);
      } catch (error) {
        console.error("Error fetching motion/daylight data:", error);
        setGlobalSelectedShape(selectedShape);
      } finally {
        setIsLoadingMotionAndDaylightValue(false);
      }
    })();

    await Promise.allSettled([assetDetailsPromise, motionAndDaylightPromise]);
  };

  /*   useEffect(() => {
    if (globalSelectedShape?.assetCode) {
      dispatch(
        getAssetSingleAssetByCode({ assetCode: globalSelectedShape?.assetCode })
      );
    }
  }, [globalSelectedShape]);

  useEffect(() => {
    if (assetDetails?.siteCode) {
      dispatch(getBlocksBySiteCode({ siteCode: assetDetails?.siteCode }));
    }
  }, [assetDetails]); */

  const handleDragMove = (id: any, x: any, y: any) => {
    dispatch(setUnsavedDetectorInitiated(true));
    setDraggingShapeId(id); // Set the currently dragging shape
    const rawX = x / assetScale.x;
    const rawY = y / assetScale.y;
    const updatedShapes = shapes.map((shape: any) => {
      return shape.id === id ? { ...shape, x: rawX, y: rawY } : shape;
    });

    setShapes(updatedShapes);

    setTooltip({
      visible: false,
      content: "",
      selectedItemId: null,
      x: 0,
      y: 0,
    });
    setGlobalSelectedShape(null);
  };

  const handleDragEnd = (id: any, x: any, y: any) => {
    setDraggingShapeId(null); // Clear dragging state
    const updatedShapes = shapes?.map((shape: any) => {
      if (shape?.id === id) {
        // Adjust coordinates to account for the offset that will be applied after dragging
        const parentAssetTypeId = shape?.parentAssetTypeId?.toLowerCase();

        // "1" and "44" for photocell sensor, "3" for gateway
        const adjustedX =
          parentAssetTypeId === "3"
            ? x - dynamicGatewayOffset
            : parentAssetTypeId === "1" || parentAssetTypeId === "44"
              ? x - dynamicPhotocellOffset
              : x - circlePositionOffset - dynamicCircle / 2;
        const adjustedY =
          parentAssetTypeId === "3"
            ? y - dynamicGatewayOffset
            : parentAssetTypeId === "1" || parentAssetTypeId === "44"
              ? y - dynamicPhotocellOffset
              : y - circlePositionOffset - dynamicCircle / 2;

        const rawUpdatedX = adjustedX / assetScale.x;
        const rawUpdatedY = adjustedY / assetScale.y;
        const updatedData = { ...shape, x: rawUpdatedX, y: rawUpdatedY, floorCode: floor };

        const indexToRemove = changedShapes.findIndex(
          (item: any) => item.id === id
        );

        if (indexToRemove !== -1) {
          changedShapes.splice(indexToRemove, 1);
        }

        const presentData = [...changedShapes, updatedData];
        setChangedShapes(presentData);
      }

      return shape.id === id
        ? {
          ...shape,
          x:
            shape?.parentAssetTypeId?.toLowerCase() === "3" // Gateway
              ? (x - dynamicGatewayOffset) / assetScale?.x
              : shape?.parentAssetTypeId?.toLowerCase() === "1" || shape?.parentAssetTypeId?.toLowerCase() === "44" // Photocell Sensor
                ? (x - dynamicPhotocellOffset) / assetScale?.x
                : (x - circlePositionOffset - dynamicCircle / 2) /
                assetScale.x,
          y:
            shape?.parentAssetTypeId?.toLowerCase() === "3" // Gateway
              ? (y - dynamicGatewayOffset) / assetScale?.y
              : shape?.parentAssetTypeId?.toLowerCase() === "1" || shape?.parentAssetTypeId?.toLowerCase() === "44" // Photocell Sensor
                ? (y - dynamicPhotocellOffset) / assetScale?.y
                : (y - circlePositionOffset - dynamicCircle / 2) /
                assetScale?.y,
        }
        : shape;
    });

    setShapes(updatedShapes);

    setTooltip({
      visible: false,
      content: "",
      selectedItemId: null,
      x: 0,
      y: 0,
    });
    setGlobalSelectedShape(null);
  };

  const handleUpdateAssetTooltip = () => {
    setIsUpdateButtonInTooltipVisible(false);

    storeLocations?.forEach((location: any) => {
      const updatedBlockCode =
        location?.blockCode || (assetDetails ? assetDetails?.blockCode : null);
      const updatedFloorCode =
        location?.floorCode || (assetDetails ? assetDetails?.floorCode : null);
      const displayLocationId =
        location?.displayLocationId ||
        globalSelectedShape?.displayLocationId ||
        null;

      dispatch(
        updateAssetLocation({
          assetCode: location?.assetCode,
          displayLocationId: displayLocationId,
          blockCode: updatedBlockCode,
          floorCode: updatedFloorCode,
        })
      ).then(() => {
        dispatch(setAssetRemovedOrUpdated(Date.now()));

        // Update local shapes state instead of reloading entire floor plan
        setShapes((prevShapes) =>
          prevShapes.map((shape) =>
            shape.assetCode === location?.assetCode
              ? {
                ...shape,
                displayLocationId: displayLocationId,
                floorLocation:
                  locations?.find(
                    (loc: any) => loc.value === displayLocationId
                  )?.title || shape.floorLocation,
              }
              : shape
          )
        );

        // Update globalSelectedShape locally
        if (globalSelectedShape?.assetCode === location?.assetCode) {
          setGlobalSelectedShape((prev: any) => ({
            ...prev,
            displayLocationId: displayLocationId,
          }));
        }

        setStoreLocations([]);
        setTooltip({ ...tooltip, visible: false });
      });
    });
  };

  const handleSubmitCanvas = async () => {
    const locationUpdatePromises: Promise<any>[] = [];

    // Collect all location update promises without triggering floor reload
    storeLocations?.forEach((location: any) => {
      const promise = dispatch(
        updateAssetLocation({
          assetCode: location?.assetCode,
          displayLocationId: location?.displayLocationId,
          blockCode: location?.blockCode,
          floorCode: location?.floorCode,
        })
      );
      locationUpdatePromises.push(promise);
    });

    // Wait for all location updates to complete
    if (locationUpdatePromises.length > 0) {
      await Promise.all(locationUpdatePromises);
    }

    setStoreLocations([]);
    setGlobalSelectedShape(null);

    if (changedShapes?.length > 0) {
      const similarFloors = selectedSimilarFloors?.map((similarFloor: any) => {
        return similarFloor.key;
      });

      try {
        await dispatch(
          updateFloorPlan({
            payload: {
              updatedShapes: changedShapes,
              similarFloors,
              blockCode,
            },
          })
        );

        dispatch(setAssetRemovedOrUpdated(Date.now()));
        dispatch(setAttemptedRoute(""));
        dispatch(setUnsavedDetectorInitiated(false));
        // setIsUnsavedChanges(false);

        // Clear changedShapes after successful save
        setChangedShapes([]);

        toast.success("Floorplan updated", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnFocusLoss: true,
          draggable: true,
          pauseOnHover: true,
        });

        if (floor) {
          navigate(
            `/dashboard/${btoa(blockCode)}/floorplan/${encodeURIComponent(
              floor
            )}`
          );
        }
      } catch (error) {
        console.error("Error updating floorplan:", error);
        toast.error("Failed to update floorplan", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    }
    // else if (locationUpdatePromises.length > 0) {
    //   // Only location updates were made, no shape changes
    //   dispatch(setAttemptedRoute(""));
    //   dispatch(setUnsavedDetectorInitiated(false));
    //   // console.log("Asset locations updated");

    // add a toster here if needed

    //   if (floor) {
    //     navigate(
    //       `/dashboard/${btoa(blockCode)}/floorplan/${encodeURIComponent(
    //         floor
    //       )}`
    //     );
    //   }
    // } 
    else {
      dispatch(setAttemptedRoute(""));
      dispatch(setUnsavedDetectorInitiated(false));
      toast.warning("Sorry!! No changes detected", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnFocusLoss: true,
        draggable: true,
        pauseOnHover: true,
      });
    }
  };

  const handleDeleteAsset = () => {
    const assetCodeToRemove = globalSelectedShape?.assetCode;

    dispatch(
      removeAssetFloor({
        assetCode: assetCodeToRemove,
      })
    ).then(() => {
      dispatch(setAssetRemovedOrUpdated(Date.now()));

      // Update local shapes state instead of reloading entire floor plan
      // This preserves newly placed assets that haven't been saved yet
      setShapes((prevShapes) =>
        prevShapes.filter((shape) => shape.assetCode !== assetCodeToRemove)
      );

      // Also remove from changedShapes to keep them in sync
      setChangedShapes((prevChangedShapes) =>
        prevChangedShapes.filter(
          (shape) => shape.assetCode !== assetCodeToRemove
        )
      );

      setIsDeleteAssetModal(false);
      setGlobalSelectedShape(null);
      setTooltip({ ...tooltip, visible: false });
    });
  };

  const handleAssetPropertyChange = (fieldName: string, value: any) => {
    setStoreLocations((prev: any) => {
      const existingIndex = prev.findIndex(
        (item: any) => item.assetCode === globalSelectedShape?.assetCode
      );

      let updatedAsset;
      if (existingIndex !== -1) {
        // Update existing item's field
        updatedAsset = { ...prev[existingIndex], [fieldName]: value };
        const updated = [...prev];
        updated[existingIndex] = updatedAsset;

        // Check if any field has changed from original assetDetails
        const hasChanges =
          (updatedAsset.displayLocationId !== undefined &&
            Number(updatedAsset.displayLocationId) !==
            Number(assetDetails?.displayLocationId)) ||
          (updatedAsset.blockCode !== undefined &&
            updatedAsset.blockCode !== assetDetails?.blockCode) ||
          (updatedAsset.floorCode !== undefined &&
            updatedAsset.floorCode !== assetDetails?.floorCode);
        setIsUpdateButtonInTooltipVisible(hasChanges);
        return updated;
      } else {
        // Add new item with the changed field
        updatedAsset = {
          assetCode: globalSelectedShape?.assetCode,
          [fieldName]: value,
        };

        // For new items, check if the new value differs from assetDetails
        const hasChanges =
          (fieldName === "displayLocationId" &&
            value !== assetDetails?.displayLocationId) ||
          (fieldName === "blockCode" && value !== assetDetails?.blockCode) ||
          (fieldName === "floorCode" && value !== assetDetails?.floorCode);

        setIsUpdateButtonInTooltipVisible(hasChanges);
        return [...prev, updatedAsset];
      }
    });
  };

  const onChangeAssetLocation = (selectedLocation: any) => {
    handleAssetPropertyChange("displayLocationId", selectedLocation);
  };

  const onChangeAssetBlock = (blockCode: any) => {
    handleAssetPropertyChange("blockCode", blockCode);
  };

  const onChangeAssetLevel = (floorCode: any) => {
    handleAssetPropertyChange("floorCode", floorCode);
  };

  const getSelectedAssetProperty = (fieldName: string, fallbackValue: any) => {
    const pendingAsset = storeLocations.find(
      (item: any) => item.assetCode === globalSelectedShape?.assetCode
    );

    return pendingAsset?.[fieldName] ?? fallbackValue ?? "";
  };

  /* const modalActionConfig: any = [
    {
      label: "Yes",
      event: handleSubmitCanvas,
      variant: "primary",
    },
  ]; */

  const deleteAssetModalActionConfig: any = [
    {
      label: "Yes",
      event: handleDeleteAsset,
      variant: "primary",
    },
  ];

  const handle = useFullScreenHandle();

  const handleFullScreenClose = (currentState: boolean) => {
    if (!currentState) {
      setZoomLevel(1);
      updateContainerDimensions();
      setGlobalSelectedShape(null);
    }
  };

  const [file, setFile] = useState(null as any);
  const [progress, setProgress] = useState<number>(0);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
      setProgress(0);

      const isValid = await validateImageFile(event.target.files[0], false);
      if (!isValid) {
        event.target.value = "";
        return;
      }

      try {
        // Create a params object
        const params = {
          blockCode: blockCode || "",
          Id: floorDetails?.data?.id || "",
          FloorCode: floorDetails?.data?.floorCode || "",
        };

        const formData = new FormData();
        formData.append("UploadedFloorImage", event.target.files[0]);

        await axiosInstance
          .post(`/slpapp/BlockToFloor`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            params, // Include params in the API request
            onUploadProgress: (progressEvent) => {
              const { loaded, total } = progressEvent;
              if (total !== null && total !== undefined) {
                const percentCompleted = Math.round((loaded * 100) / total);
                setProgress(percentCompleted);
              }
            },
          })
          .then((response: any) => {
            if (response.data.status === 201) {
              toast.success(response.data.message, {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnFocusLoss: true,
                draggable: true,
                pauseOnHover: true,
              });
            }

            dispatch(getFloorDetails({ floorCode: floor, blockCode }));
            dispatch(getFloorAssetsAndShapes({ floorCode: floor, blockCode }));
          })
          .catch((error: any) => {
            setProgress(0);
          });
      } catch (error) {
        setProgress(0);
        console.error("Error uploading file:", error);
        throw error;
      }
    }

    event.target.value = "";
  };

  const getAssetUrl = (assetCode: string) => `/asset/${btoa(assetCode)}`;

  const [isDeleteAssetModal, setIsDeleteAssetModal] = useState(false);

  const [isHoveredTooltipElement, setIsHoveredTooltipElement] = useState(false);

  const closeToolTip = () => {
    setIsUpdateButtonInTooltipVisible(false);
    setStoreLocations([]);
    if (!isHoveredTooltipElement && !hovered) {
      setTooltip({ ...tooltip, visible: false });
      if (!isDeleteAssetModal) {
        setGlobalSelectedShape(null);
      }
    }
  };

  const { ref: toolTipRef, isClickedOutside: isOutside } =
    useOutsideClick(closeToolTip);

  // Handle control panel position based on screen height
  const handleControlPanelPosition = (innerHeight: number) => {
    if (innerHeight > 1180) {
      return 180; // Margin for larger screens
    } else if (innerHeight <= 1180 && innerHeight > 1024) {
      return 200; // Margin for medium screens
    } else {
      return 220; // Margin for smaller screens
    }
  };

  const [controlPanelBottom, setcontrolPanelBottom] = useState(
    handleControlPanelPosition(window.innerHeight)
  );

  // Responsive search bar length state
  // Function to determine initial search bar length based on window width
  const getInitialSearchBarLength = () => {
    if (window.innerWidth <= 768) return "350px";
    if (window.innerWidth <= 992) return "260px";
    if (window.innerWidth <= 1200) return "385px";
    return "540px";
  };
  const [searchBarLength, setSearchBarLength] = useState(
    getInitialSearchBarLength()
  );

  // handle image when height is more than width
  const { width, height, assetScale } = useMemo(
    () => calculateImageDimensions(image),
    [image]
  );

  const scaledShapes = useMemo(() => {
    if (!shapes?.length) return [];

    return shapes.map((shape: FloorShape) => ({
      ...shape,
      x: (shape?.x ?? 0) * assetScale.x,
      y: (shape?.y ?? 0) * assetScale.y,
    }));
  }, [shapes, assetScale]);

  useEffect(() => {
    const handleResize = () => {
      setcontrolPanelBottom(handleControlPanelPosition(window.innerHeight));
      setSearchBarLength(getInitialSearchBarLength());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div>
      <div>
        <div
          className={`d-flex flex-row justify-content-between gap-3 mb-3 align-items-center ${handle?.active ? "me-5" : ""
            }`}
        >
          <div className="canvas-container">
            <B2Button
              disabled={changedShapes?.length > 0}
              variant="primary"
              className="d-block cursor-pointer"
              onClick={() => {
                navigate(pathname);
              }}
            >
              <FontAwesomeIcon icon={faEye} className="fs-7 me-2" />
              Back To View
            </B2Button>
            <HasPermission
              moduleName="Floor Plan Detail"
              actionName="Update-Floor Plan Detail"
              isRoute={false}
            >
              <B2Button
                variant="primary"
                className="ms-2"
                onClick={handleSubmitCanvas}
                disabled={!changedShapes || changedShapes?.length <= 0}
              >
                Update
              </B2Button>
            </HasPermission>
          </div>

          <div
            style={{
              position: "relative",
              marginTop: "2px",
            }}
          >
            <DashboardFloorplanWildcardSearchingComponent
              isUpdateFloorPlan={true}
              setSelectedSearchedAssetCodeForHighlighting={
                setSelectedAssetCodeForHighlight
              }
              searchBarLength={searchBarLength}
            />
          </div>

          <HasPermission
            moduleName="Floor Plan Detail"
            actionName="Update-Floor Plan Detail"
            isRoute={false}
          >
            <B2Button className="icon-text-btn" variant="primary">
              <FontAwesomeIcon icon={faUpload} className="" />
              <label htmlFor="upload" className="ms-2 cursor-pointer">
                Import Floor Image{" "}
                <input
                  id="upload"
                  type="file"
                  accept={imageFileTypes}
                  className="d-none"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleFileChange(event);
                  }}
                />
              </label>
            </B2Button>
          </HasPermission>
        </div>

        <FullScreen
          handle={handle}
          onChange={(state: boolean) => {
            handleFullScreenClose(state);
          }}
        >
          <div id="canvas">
            <SkeletonComponent
              wrapperHeight="570px"
              skeletonHeight="570px"
              isLoading={
                isLoadingFloorDetails || isLoadingFloorAssetsAndShapes
              }
            >
              {image &&
                gatewayImageFileFault &&
                gatewayImageFileOffline &&
                gatewayImageFileInitial &&
                gatewayImageFileOnline &&
                photocellImageFileOnline &&
                photocellImageFileOffline &&
                photocellImageFileOff &&
                onFaultImageFile &&
                onInitialImageFile &&
                onOfflineImageFile &&
                onOnlineImageFile &&
                offFaultImageFile &&
                offInitialImageFile &&
                offOfflineImageFile &&
                offOnlineImageFile &&
                containerDimensions?.width ? (
                <div className="canvas-container position-relative">
                  <Stage
                    width={containerDimensions.width}
                    height={containerDimensions.height}
                    scaleX={zoomLevel}
                    scaleY={zoomLevel}
                    ref={stageRef}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onWheel={handleStageWheel}
                    className="stage-wrapper"
                  >
                    <Layer
                      y={15}
                      draggable
                      isLayer={true}
                      ref={layerRef}
                      onClick={handleLayerSelect}
                      onTap={handleLayerSelect}
                      onDragEnd={(e) => {
                        const target = e.target;
                        if (target.attrs.isLayer) {
                          handleStageDrag(e.target.x(), e.target.y());
                        }
                      }}
                    >
                      {image && (
                        <Image image={image} width={width} height={height} />
                      )}
                      {scaledShapes?.map((shape: FloorShape) => {
                        // "1" and "44" for photocell sensor, "3" for gateway
                        const parentAssetTypeId =
                          shape?.parentAssetTypeId?.toLowerCase();
                        const isGateway = parentAssetTypeId === "3";
                        const isPhotocell =
                          parentAssetTypeId === "1" ||
                          parentAssetTypeId === "44";
                        return (
                          <React.Fragment key={shape.id}>
                            {isGateway || isPhotocell ? (
                              <Image
                                name={
                                  isGateway
                                    ? "gateway-image"
                                    : "photocell-image"
                                }
                                x={
                                  draggingShapeId === shape.id
                                    ? shape.x
                                    : shape.x +
                                    (isGateway
                                      ? dynamicGatewayOffset
                                      : dynamicPhotocellOffset)
                                }
                                y={
                                  draggingShapeId === shape.id
                                    ? shape.y
                                    : shape.y +
                                    (isGateway
                                      ? dynamicGatewayOffset
                                      : dynamicPhotocellOffset)
                                }
                                image={
                                  isGateway
                                    ? shape?.status === "On" ||
                                      shape?.status === "Online"
                                      ? gatewayImageFileOnline
                                      : shape?.status === "N/A" ||
                                        shape?.status === "Offline" ||
                                        shape?.status === "Off"
                                        ? gatewayImageFileInitial
                                        : gatewayImageFileFault
                                    : shape?.status === "On" ||
                                      shape?.status === "Online"
                                      ? photocellImageFileOnline
                                      : shape?.status === "N/A" ||
                                        shape?.status === "Offline" ||
                                        shape?.status === "Off"
                                        ? photocellImageFileOffline
                                        : photocellImageFileOff
                                }
                                width={
                                  isGateway
                                    ? dynamicGatewaySize
                                    : dynamicPhotocellSize
                                }
                                height={
                                  isGateway
                                    ? dynamicGatewaySize
                                    : dynamicPhotocellSize
                                }
                                draggable={isDraggingItemsEnable}
                                dragBoundFunc={(pos) => {
                                  const newX = Math.max(
                                    0,
                                    Math.min(
                                      pos.x,
                                      containerDimensions.width - 50
                                    )
                                  );
                                  const newY = Math.max(
                                    0,
                                    Math.min(
                                      pos.y,
                                      containerDimensions.height - 50
                                    )
                                  );
                                  return { x: newX, y: newY };
                                }}
                                onDragMove={(e) =>
                                  handleDragMove(
                                    shape.id,
                                    e.target.x(),
                                    e.target.y()
                                  )
                                }
                                onDragEnd={(e) =>
                                  handleDragEnd(
                                    shape.id,
                                    e.target.x(),
                                    e.target.y()
                                  )
                                }
                                onClick={(e: any) => {
                                  setIsLoadingMotionAndDaylightValue(true);
                                  setIsLoadingAssetDetails(true);
                                  setIsLoadingBlocksBySiteCode(true);
                                  handleOnClick(shape?.id, e);
                                }}
                                onTap={(e: any) => {
                                  setIsLoadingMotionAndDaylightValue(true);
                                  setIsLoadingAssetDetails(true);
                                  setIsLoadingBlocksBySiteCode(true);
                                  handleOnClick(shape?.id, e);
                                }}
                                onMouseEnter={(e: any) =>
                                  handleShapeMouseEnter({
                                    e,
                                    shape,
                                    shapes,
                                    hoverTimeoutRef,
                                    HOVER_DELAY,
                                    setHovered,
                                    setSelectedWhileHovered,
                                    setHoverState,
                                    // newValueOnStageMove,
                                    // zoomLevel,
                                    // dynamicGatewayOffset,
                                    // circlePositionOffset,
                                  })
                                }
                                onMouseLeave={(e: any) =>
                                  handleShapeMouseLeave({
                                    e,
                                    hoverTimeoutRef,
                                    setHovered,
                                    setHoverState,
                                    setSelectedWhileHovered,
                                  })
                                }
                              />
                            ) : (
                              <>
                                {shape?.isLightOn ? (
                                  <Circle
                                    name="asset-circle"
                                    x={
                                      draggingShapeId === shape.id
                                        ? shape.x
                                        : shape.x +
                                        circlePositionOffset +
                                        dynamicCircle / 2
                                    }
                                    y={
                                      draggingShapeId === shape.id
                                        ? shape.y
                                        : shape.y +
                                        circlePositionOffset +
                                        dynamicCircle / 2
                                    }
                                    radius={dynamicCircle / 2}
                                    fill={
                                      shape.status === "On" || shape.status === "Online"
                                        ? lightOnColors.onOnlineImageFile
                                        : shape.status === "N/A" || shape.status === "Offline" || shape.status === "Off"
                                          ? lightOnColors.onInitialImageFile
                                          : shape?.status === "Fault"
                                            ? lightOnColors.onFaultImageFile
                                            : lightOnColors.onOfflineImageFile
                                              .color
                                    }
                                    stroke={
                                      shape.status === "On" ||
                                        shape.status === "Online" ||
                                        shape.status === "N/A" ||
                                        shape.status === "Offline" ||
                                        shape.status === "Off" ||
                                        shape?.status === "Fault"
                                        ? lightOnColors.border
                                        : lightOnColors.onOfflineImageFile
                                          .border
                                    }
                                    strokeWidth={Math.max(
                                      0.1,
                                      0.5 / zoomLevel
                                    )}
                                    draggable={isDraggingItemsEnable}
                                    dragBoundFunc={(pos) => {
                                      const newX = Math.max(
                                        0,
                                        Math.min(
                                          pos.x,
                                          containerDimensions.width - 50
                                        )
                                      );
                                      const newY = Math.max(
                                        0,
                                        Math.min(
                                          pos.y,
                                          containerDimensions.height - 50
                                        )
                                      );
                                      return { x: newX, y: newY };
                                    }}
                                    onDragMove={(e) =>
                                      handleDragMove(
                                        shape.id,
                                        e.target.x(),
                                        e.target.y()
                                      )
                                    }
                                    onDragEnd={(e) =>
                                      handleDragEnd(
                                        shape.id,
                                        e.target.x(),
                                        e.target.y()
                                      )
                                    }
                                    onClick={(e: any) => {
                                      setIsLoadingMotionAndDaylightValue(
                                        true
                                      );
                                      setIsLoadingAssetDetails(true);
                                      setIsLoadingBlocksBySiteCode(true);
                                      handleOnClick(shape?.id, e);
                                    }}
                                    onTap={(e: any) => {
                                      setIsLoadingMotionAndDaylightValue(
                                        true
                                      );
                                      setIsLoadingAssetDetails(true);
                                      setIsLoadingBlocksBySiteCode(true);
                                      handleOnClick(shape?.id, e);
                                    }}
                                    onMouseEnter={(e: any) =>
                                      handleShapeMouseEnter({
                                        e,
                                        shape,
                                        shapes,
                                        hoverTimeoutRef,
                                        HOVER_DELAY,
                                        setHovered,
                                        setSelectedWhileHovered,
                                        setHoverState,
                                        // newValueOnStageMove,
                                        // zoomLevel,
                                        // dynamicGatewayOffset,
                                        // circlePositionOffset,
                                      })
                                    }
                                    onMouseLeave={(e: any) =>
                                      handleShapeMouseLeave({
                                        e,
                                        hoverTimeoutRef,
                                        setHovered,
                                        setHoverState,
                                        setSelectedWhileHovered,
                                      })
                                    }
                                  />
                                ) : (
                                  <Circle
                                    name="asset-circle"
                                    x={
                                      draggingShapeId === shape.id
                                        ? shape.x
                                        : shape.x +
                                        circlePositionOffset +
                                        dynamicCircle / 2
                                    }
                                    y={
                                      draggingShapeId === shape.id
                                        ? shape.y
                                        : shape.y +
                                        circlePositionOffset +
                                        dynamicCircle / 2
                                    }
                                    radius={dynamicCircle / 2}
                                    fill={
                                      shape?.status === "On" || shape?.status === "Online"
                                        ? lightOffColors.offOnlineImageFile
                                          .color
                                        : shape?.status === "N/A" || shape?.status === "Offline" || shape?.status === "Off"
                                          ? lightOffColors.offInitialImageFile
                                          : shape?.status === "Fault"
                                            ? lightOffColors.offFaultImageFile
                                            : lightOffColors.offOfflineImageFile
                                    }
                                    stroke={
                                      shape.status === "On" || shape?.status === "Online"
                                        ? lightOffColors.offOnlineImageFile
                                          .border
                                        : lightOffColors.border
                                    }
                                    strokeWidth={Math.max(
                                      0.1,
                                      0.5 / zoomLevel
                                    )}
                                    draggable={isDraggingItemsEnable}
                                    dragBoundFunc={(pos) => {
                                      const newX = Math.max(
                                        0,
                                        Math.min(
                                          pos.x,
                                          containerDimensions.width - 50
                                        )
                                      );
                                      const newY = Math.max(
                                        0,
                                        Math.min(
                                          pos.y,
                                          containerDimensions.height - 50
                                        )
                                      );
                                      return { x: newX, y: newY };
                                    }}
                                    onDragMove={(e) =>
                                      handleDragMove(
                                        shape.id,
                                        e.target.x(),
                                        e.target.y()
                                      )
                                    }
                                    onDragEnd={(e) =>
                                      handleDragEnd(
                                        shape.id,
                                        e.target.x(),
                                        e.target.y()
                                      )
                                    }
                                    onClick={(e: any) => {
                                      setIsLoadingMotionAndDaylightValue(
                                        true
                                      );
                                      setIsLoadingAssetDetails(true);
                                      setIsLoadingBlocksBySiteCode(true);
                                      handleOnClick(shape?.id, e);
                                    }}
                                    onTap={(e: any) => {
                                      setIsLoadingMotionAndDaylightValue(
                                        true
                                      );
                                      setIsLoadingAssetDetails(true);
                                      setIsLoadingBlocksBySiteCode(true);
                                      handleOnClick(shape?.id, e);
                                    }}
                                    onMouseEnter={(e: any) =>
                                      handleShapeMouseEnter({
                                        e,
                                        shape,
                                        shapes,
                                        hoverTimeoutRef,
                                        HOVER_DELAY,
                                        setHovered,
                                        setSelectedWhileHovered,
                                        setHoverState,
                                        // newValueOnStageMove,
                                        // zoomLevel,
                                        // dynamicGatewayOffset,
                                        // circlePositionOffset,
                                      })
                                    }
                                    onMouseLeave={(e: any) =>
                                      handleShapeMouseLeave({
                                        e,
                                        hoverTimeoutRef,
                                        setHovered,
                                        setHoverState,
                                        setSelectedWhileHovered,
                                      })
                                    }
                                  />
                                )}
                              </>
                            )}
                          </React.Fragment>
                        );
                      })}

                      {tempPosition && selectLocationIcon && (
                        <Image
                          x={tempPosition.x - 8} // center the image
                          y={tempPosition.y - 8}
                          width={15}
                          height={15}
                          image={selectLocationIcon}
                        />
                      )}
                    </Layer>
                  </Stage>

                  {/* right top icons */}
                  <div
                    className="flex flex-col justify-between"
                    style={{
                      width: "50px",
                      height: "100px",
                      position: "absolute",
                      top: handle?.active
                        ? 200
                        : 15,
                      right: 0,
                    }}
                  >
                    <div className="d-flex flex-col align-items-center">
                      <div
                        style={{
                          width: "30px",
                          display: "flex",
                          justifyContent: "center",
                          border: "2px solid 1976D2",
                          borderTopLeftRadius: "8px",
                          borderTopRightRadius: "8px",
                          padding: "0 0.4rem 0 0.4rem",
                        }}
                      >
                        <button
                          type="button"
                          className="cursor-pointer"
                          onClick={handleZoomIn}
                          title="Zoom In"
                          aria-label="Zoom In"
                          style={{
                            padding: 0,
                            border: "none",
                            background: "transparent",
                            display: "flex",
                          }}
                        >
                          <img
                            className="mb-1"
                            style={{ width: "25px" }}
                            src={zoomIn}
                            alt=""
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                      <div
                        style={{
                          width: "30px",
                          display: "flex",
                          justifyContent: "center",
                          border: "2px solid 1976D2",
                          borderBottomLeftRadius: "8px",
                          borderBottomRightRadius: "8px",
                          borderTop: "none",
                          padding: "0 0.4rem 0.2rem 0.4rem",
                          marginTop: "10px"
                        }}
                      >
                        <button
                          type="button"
                          className="cursor-pointer"
                          onClick={handleZoomOut}
                          title="Zoom Out"
                          aria-label="Zoom Out"
                          style={{
                            padding: 0,
                            border: "none",
                            background: "transparent",
                            display: "flex",
                          }}
                        >
                          <img
                            style={{ width: "25px" }}
                            src={zoomOut}
                            alt=""
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                      {zoomLevel !== 1 && (
                        <div>
                          <button
                            className="btn pb-0"
                            style={{
                              marginRight: "0.28rem",
                              padding: "0.5rem",
                              marginTop: "7px"
                            }}
                            onClick={handleResetZoom}
                            title="Reset Zoom"
                          >
                            <FontAwesomeIcon
                              icon={faRotateRight}
                              style={{ fontSize: "1.35rem" }}
                            />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* right top icons ends */}

                  {/* right bottom icons */}
                  <div
                    className="flex flex-col justify-between"
                    style={{
                      width: "50px",
                      height: "100px",
                      position: "absolute",
                      bottom: handle?.active
                        ? 200
                        : 10,
                      right: 0,
                    }}
                  >
                    <div className="d-flex flex-col align-items-center">
                      <button
                        type="button"
                        className="cursor-pointer"
                        onClick={() => {
                          setIsDraggingItemsEnable((pre: boolean) => !pre);
                        }}
                        title={
                          isDraggingItemsEnable
                            ? "Items are draggable, click to lock"
                            : "Items are locked, click to unlock"
                        }
                        aria-label={
                          isDraggingItemsEnable
                            ? "Items are draggable, click to lock"
                            : "Items are locked, click to unlock"
                        }
                        aria-pressed={isDraggingItemsEnable}
                        style={{
                          padding: 0,
                          border: "none",
                          background: "transparent",
                          display: "flex",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={isDraggingItemsEnable ? faLockOpen : faLock}
                          style={{
                            fontSize: "30px",
                            height: "24px",
                            width: "24px",
                            marginBottom: "15px",
                            marginRight: "3px",
                          }}
                        />
                      </button>

                      <button
                        type="button"
                        className="cursor-pointer"
                        onClick={() => {
                          setIsSelectMode(!isSelectMode);
                          if (isSelectMode) {
                            setTempPosition(null); // Clear temp position when turning off select mode
                          }
                        }}
                        title={isSelectMode ? "Exit Select Mode" : "Select Position"}
                        aria-label={isSelectMode ? "Exit Select Mode" : "Select Position"}
                        aria-pressed={isSelectMode}
                        style={{
                          padding: 0,
                          border: "none",
                          background: "transparent",
                          display: "flex",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faCrosshairs}
                          style={{
                            color: isSelectMode ? "#1976D2" : "#000",
                            fontSize: "30px",
                            height: "24px",
                            width: "24px",
                            marginBottom: "6px",
                            marginRight: "3px",
                          }}
                        />
                      </button>

                      <div>
                        {handle?.active ? (
                          <button
                            type="button"
                            className="cursor-pointer"
                            onClick={() => handle.exit()}
                            style={{
                              marginRight: "4px",
                              marginTop: "10px",
                              height: "18px",
                              width: "18px",
                              display: "flex",
                              padding: 0,
                              border: "none",
                              background: "transparent",
                            }}
                            title="Exit Full Screen"
                            aria-label="Exit Full Screen"
                          >
                            <img
                              style={{ height: "100%", width: "100%" }}
                              src={exitFullScreen}
                              alt=""
                              aria-hidden="true"
                            />
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="cursor-pointer"
                            onClick={() => {
                              handle.enter();
                              setZoomLevel(1.5);
                              setContainerDimensions({
                                height: window.innerHeight + 100,
                                width: window.innerWidth - 100,
                              });
                              setGlobalSelectedShape(null);
                            }}
                            style={{
                              marginRight: "4px",
                              marginTop: "10px",
                              height: "18px",
                              width: "18px",
                              display: "flex",
                              padding: 0,
                              border: "none",
                              background: "transparent",
                            }}
                            title="View In Full Screen"
                            aria-label="View In Full Screen"
                          >
                            <img
                              style={{ height: "100%", width: "100%" }}
                              src={fullScreen}
                              alt=""
                              aria-hidden="true"
                            />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* right bottom icons ends */}
                  {tooltip?.visible &&
                    globalSelectedShape &&
                    shapes?.find(
                      (shape) => shape.id === tooltip.selectedItemId
                    ) && (
                      <div
                        ref={toolTipRef}
                        onMouseEnter={() =>
                          setIsHoveredTooltipElement(true)
                        }
                        onMouseLeave={() =>
                          setIsHoveredTooltipElement(false)
                        }
                        style={{
                          position: "absolute",
                          top: handle?.active
                            ? tooltip?.y -
                            (newValueOnStageMove.y ? 238 : 218)
                            : tooltip.y -
                            (newValueOnStageMove.y ? 310 : 295),
                          left: handle?.active
                            ? tooltip?.x - 55
                            : tooltip.x - 97,
                          zIndex: 9999,
                          width: "400px",
                        }}
                      >
                        <div
                          className="floortooltip"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            height:
                              globalSelectedShape?.status == "Fault" ||
                                globalSelectedShape?.assetTypeId != 3
                                ? "285px"
                                : "255px",
                            width: "max-content",
                            zIndex: "999",
                            position: "relative",
                            borderRadius: "5px",
                            backgroundColor: "white",
                            boxShadow:
                              "0 -3px 3px -3px #e0e0e0, 3px 0px 3px -3px #d1d1d1, 5px 5px 3px -5px #e5e5e5",
                            padding: " 0.5rem 1rem",
                            lineHeight: "25px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <div className="d-flex">
                              <div className="tooltip-fs">Asset ID: </div>
                              {globalSelectedShape?.assetCode && (
                                <a
                                  href={getAssetUrl(
                                    globalSelectedShape.assetCode,
                                  )}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "#1976D2",
                                    cursor: "pointer",
                                  }}
                                  className="ms-2 tooltip-fs"
                                >
                                  {globalSelectedShape.assetCode}{" "}
                                </a>
                              )}
                            </div>
                            <div className="d-flex tooltip-fs">
                              <div className="tooltip-fs">
                                Display Name:{" "}
                              </div>
                              <div className="ms-2 tooltip-fs">
                                {globalSelectedShape?.sensorDisplayName}{" "}
                              </div>
                            </div>
                            <div className="d-flex align-items-center">
                              <div className="tooltip-fs">Location: </div>
                              <div className="ms-2 tooltip-fs">
                                <SkeletonComponent
                                  wrapperHeight="30px"
                                  skeletonHeight="30px"
                                  width="140px"
                                  isLoading={
                                    isLoadingLocations ||
                                    isLoadingAssetDetails
                                  }
                                  count={1}
                                >
                                  <SelectComponent
                                    selectedValue={getSelectedAssetProperty(
                                      "displayLocationId",
                                      assetDetails?.displayLocationId
                                    )}
                                    options={locations}
                                    placeHolder="Select Location"
                                    onChange={onChangeAssetLocation}
                                    customeStyle={true}
                                  />
                                </SkeletonComponent>
                              </div>
                            </div>
                            <div className="d-flex align-items-center mt-1">
                              <div className="tooltip-fs">
                                Display Level:{" "}
                              </div>
                              <div className="ms-2 tooltip-fs">
                                <SkeletonComponent
                                  wrapperHeight="30px"
                                  skeletonHeight="30px"
                                  width="140px"
                                  isLoading={
                                    isLoadingFloorByBlockCode ||
                                    isLoadingAssetDetails
                                  }
                                  count={1}
                                >
                                  <SelectComponent
                                    selectedValue={getSelectedAssetProperty(
                                      "floorCode",
                                      assetDetails?.floorCode
                                    )}
                                    options={levelOptions}
                                    placeHolder="Select Level"
                                    onChange={onChangeAssetLevel}
                                    customeStyle={true}
                                  />
                                </SkeletonComponent>
                              </div>
                            </div>

                            <div className="d-flex align-items-center mt-1">
                              <div className="tooltip-fs">
                                Display Block:{" "}
                              </div>
                              <div className="ms-2 tooltip-fs">
                                <SkeletonComponent
                                  wrapperHeight="30px"
                                  skeletonHeight="30px"
                                  width="140px"
                                  isLoading={
                                    isLoadingBlocksBySiteCode ||
                                    isLoadingAssetDetails
                                  }
                                  count={1}
                                >
                                  <SelectComponent
                                    selectedValue={getSelectedAssetProperty(
                                      "blockCode",
                                      assetDetails?.blockCode
                                    )}
                                    options={locationOptions}
                                    placeHolder="Select Block"
                                    onChange={onChangeAssetBlock}
                                    customeStyle={true}
                                  />
                                </SkeletonComponent>
                              </div>
                            </div>

                            <div className="d-flex">
                              <div className="tooltip-fs">Light Type: </div>
                              <div className="ms-2 tooltip-fs">
                                {globalSelectedShape?.lightType}{" "}
                              </div>
                            </div>

                            {globalSelectedShape?.assetTypeId != 3 ? (
                              <div className="d-flex tooltip-fs">
                                <div className="tooltip-fs">
                                  Motion Count:{" "}
                                </div>
                                <div className="ms-2 tooltip-fs">
                                  {isLoadingMotionAndDaylightValue ? (
                                    <Spinner
                                      animation="border"
                                      variant="primary"
                                      size="sm"
                                      className="ms-4"
                                    />
                                  ) : (
                                    <>
                                      {globalSelectedShape?.motionCount ?? "N/A"}
                                    </>
                                  )}
                                </div>
                              </div>
                            ) : null}

                            <div className="d-flex tooltip-fs">
                              <div className="tooltip-fs">Daylight: </div>
                              <div className="ms-2 tooltip-fs">
                                {isLoadingMotionAndDaylightValue ? (
                                  <Spinner
                                    animation="border"
                                    variant="primary"
                                    size="sm"
                                    className="ms-4"
                                  />
                                ) : (
                                  <>
                                    {globalSelectedShape?.daylight == null
                                      ? "-"
                                      : globalSelectedShape?.daylight +
                                      " Lux" || 0}
                                  </>
                                )}
                              </div>
                            </div>

                            {globalSelectedShape?.status == "Fault" ? (
                              <div className="d-flex tooltip-fs">
                                <div className="tooltip-fs">
                                  Fault Type:{" "}
                                </div>
                                <div className="ms-2 tooltip-fs">
                                  {globalSelectedShape?.faultType}{" "}
                                </div>
                              </div>
                            ) : null}
                            <div
                              className="d-flex justify-content-end  gap-3"
                              style={{ marginTop: ".5rem" }}
                            >
                              <B2Button
                                variant="danger"
                                onClick={() => {
                                  setIsDeleteAssetModal(true);
                                }}
                                size="sm"
                              >
                                Remove Asset
                              </B2Button>
                              {isUpdateButtonInTooltipVisible && (
                                <B2Button
                                  variant="primary"
                                  onClick={() => {
                                    handleUpdateAssetTooltip();
                                  }}
                                  size="sm"
                                >
                                  Update Asset
                                </B2Button>
                              )}
                            </div>
                          </div>
                          <div
                            style={{ width: "24px", position: "relative" }}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setIsUpdateButtonInTooltipVisible(false);
                                setStoreLocations([]);
                                setGlobalSelectedShape(null);
                              }}
                              title="Close"
                              aria-label="Close"
                              style={{
                                position: "absolute",
                                top: -6,
                                right: -4,
                                cursor: "pointer",
                                fontSize: "22px",
                                color: "red",
                                padding: 0,
                                border: "none",
                                background: "transparent",
                              }}
                            >
                              <FontAwesomeIcon icon={faTimes} className="fs-7 ms-2" />
                            </button>
                          </div>
                        </div>
                        <div className="updateFloorTooltipArrawContainer"></div>
                      </div>
                    )}
                </div>
              ) : (
                <div className="no-preview-container">
                  <h5>No Floor Plan to Preview</h5>
                </div>
              )}
            </SkeletonComponent>
          </div>
        </FullScreen>
      </div>

      <div>
        <div className="d-flex justify-content-center mt-4 align-items-center">
          <div>
            <img
              src={circleLightOff}
              alt="Luminaire Off"
              width="20px"
              height="20px"
            />
          </div>
          <span className="ms-1 me-4">Luminaire</span>
          <div>
            <img
              src={gatewayInitial}
              alt="Gateway"
              width="25px"
              height="30px"
            />
          </div>
          <span className="ms-1 me-4">Gateway</span>
          <div>
            <img
              src={photocellOffline}
              alt="Sensor"
              width="22px"
              height="22px"
            />
          </div>
          <span className="ms-1 me-4">Sensor</span>
        </div>

        <div className="d-flex justify-content-center mt-3 mb-5 gap-5 legend">
          <div className="d-flex justify-content-center mb-5 gap-5 legend">
            {/* <div className="d-flex gap-2"></div> */}

            <div className="d-flex gap-2">
              <img
                src={circleLightOn}
                alt="Luminaire On"
                width="18px"
                height="18px"
              />
              <p>On</p>
            </div>

            <div className="d-flex gap-2">
              <img
                src={circleLightOff}
                alt="Luminaire Off"
                width="18px"
                height="18px"
              />
              <p>Off</p>
            </div>

            <div className="d-flex gap-2">
              <img
                src={circleLightFault}
                alt="Luminaire Fault"
                width="18px"
                height="18px"
              />
              <p>Fault</p>
            </div>
          </div>
        </div>
      </div>

      {/* {false && (
          <ModalComponent
            isConformationModal={true}
            showModal={true}
            handleClose={() => false}
            headerTitle="*Unsaved Changes"
            actionConfig={modalActionConfig}
          >
            <h5 className="text-center">
              Are you sure to <b className="text-danger">Discard</b>?
            </h5>
          </ModalComponent>
        )} */}

      {isDeleteAssetModal && (
        <ModalComponent
          isConformationModal={isDeleteAssetModal}
          showModal={isDeleteAssetModal}
          handleClose={() => setIsDeleteAssetModal(false)}
          headerTitle=""
          actionConfig={deleteAssetModalActionConfig}
        >
          <h5 className="text-center">
            Are you sure to <b className="text-danger">Remove</b>{" "}
            <strong>{globalSelectedShape?.assetCode}</strong>?
          </h5>
        </ModalComponent>
      )}
    </div>
  );
};

export default UpdateFloorPlanComponent;
